//@flow
import * as Immutable from 'immutable';

import { Volume } from 'common/models/flow/Volume';
import type { VolumeModelType } from 'common/models/flow/Volume';

import I18nHelper from 'common/helpers/I18nHelper';
import { Units } from 'common/enums/UnitEnum';

import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';

import { Dimensions } from 'common/models/internal/Dimensions';

//Validation stuff
import {
    validate,
    validatable,
    validateAll,
    softValidateAll
} from 'common/utils/ValidationUtil';
import NumberUtils from 'common/utils/NumberUtils';

export const VolumeUnitType = {
    ItemCount: 'ITEM_COUNT',
    MetersCubic: 'METERS_CUBIC',
    LBHMeters: 'LBH_METERS'
};

const translationsNamespace = ['validations', 'entityManagement'];

const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

export const sourceUnitForVolume = Units.list.volume.CubicMeter;

@validatable
class InternalVolume extends Volume {
    static fromJS(json: VolumeModelType): InternalVolume {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    @validate
    setValue(value: string): this {
        return super.setValue(value);
    }

    validateValue() {
        if (!NumberUtils.isNumeric(this.value)) {
            return nsTranslate('skuValidationErrors.volumeNotANumber');
        }
        if (NumberUtils.toNumber(this.value) === 0) {
            return nsTranslate('skuValidationErrors.nonZeroVolume');
        }
        if (!NumberUtils.isPositiveNumber(this.value)) {
            return nsTranslate('skuValidationErrors.nonNumericVolume');
        }
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        return false;
    }

    @validateAll
    setAllValidationErrors(): this {
        const self = this;
        return self;
    }

    getPreferredUnit(unitConverter: UnitConversionHelper) {
        return unitConverter.getPreferredUnit({
            sourceUnit: sourceUnitForVolume
        });
    }

    getPreferredUnitText(unitConverter: UnitConversionHelper) {
        return unitConverter.getConvertedUnitDisplayText({
            sourceUnit: sourceUnitForVolume,
            pluralizeUnit: false
        });
    }

    convertToPreferredUnit(unitConverter: UnitConversionHelper): this {
        const convertValue = value =>
            unitConverter.convertVolume(NumberUtils.toNumber(value), this.unit)
                .value;
        if (this.value && !this.hasValidationErrors()) {
            return this.setValue(convertValue(this.value));
        }
    }

    convertToSourceUnit(unitConverter: UnitConversionHelper): this {
        const preferredUnit = this.getPreferredUnit(unitConverter);
        const convertValue = value =>
            unitConverter.basicConvert({
                value: NumberUtils.toNumber(value),
                sourceUnit: preferredUnit,
                targetUnit: sourceUnitForVolume
            });
        if (this.value && !this.hasValidationErrors()) {
            return this.setValue(convertValue(this.value));
        }
    }

    getTotalVolume() {
        return this.value;
    }

    toDimensions(): Dimensions {
        if (this.unit !== VolumeUnitType.LBHMeters) {
            throw new Error(
                `Can't convert to Dimensions, volume is not LBH, but: ${this.unit}`
            );
        }
        const [length, breadth, height] = this.value.split(',');
        return Dimensions.fromJS({ length, breadth, height });
    }
}

export { InternalVolume as Volume };
